import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, Trash2, User, Bookmark, ShoppingBag, Key, Layers, Heart, FileText, Plus, Copy, Pencil, Search, LayoutDashboard, LayoutGrid, Package, MessageCircle, NotebookPen } from "lucide-react";
import WorkbookSection from "@/components/account/WorkbookSection";
import NotificationBell from "@/components/NotificationBell";

// Ровно 9 пользовательских разделов кабинета Anokina Studio.
const SECTIONS = [
  { id: "overview",  label: "Обзор",           Icon: LayoutGrid },
  { id: "products",  label: "Мои продукты",    Icon: Package },
  { id: "saved",     label: "Сохранённое",     Icon: Bookmark },
  { id: "liked",     label: "Понравившиеся",   Icon: Heart },
  { id: "workbook", label: "Рабочая тетрадь",  Icon: NotebookPen },
  { id: "requests",  label: "Мои обращения",   Icon: MessageCircle },
  { id: "purchases", label: "Покупки",         Icon: ShoppingBag },
  { id: "access",    label: "Мои доступы",     Icon: Key },
  { id: "profile",   label: "Профиль",         Icon: User },
];
const SECTION_IDS = SECTIONS.map((s) => s.id);
const DEFAULT_TAB = "overview";

export default function Account() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab = SECTION_IDS.includes(tabParam) ? tabParam : DEFAULT_TAB;
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    if (tabParam && SECTION_IDS.includes(tabParam) && tabParam !== tab) setTab(tabParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);

  const changeTab = (v) => {
    if (!SECTION_IDS.includes(v)) return;
    setTab(v);
    const next = new URLSearchParams(searchParams);
    if (v === DEFAULT_TAB) next.delete("tab"); else next.set("tab", v);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="account-page">
      <header className="flex items-center gap-2 sm:gap-3 px-4 sm:px-5 pt-5 pb-3">
        <button
          onClick={() => nav("/hall")}
          data-testid="back-to-hall-button"
          className="p-2 -ml-2 rounded-full hover:bg-sand/50 shrink-0"
          aria-label="В главный зал"
        >
          <ChevronLeft className="w-5 h-5 text-espresso" />
        </button>
        <div className="flex-1 min-w-0">
          {staffLabel(user?.role) && (
            <div
              className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground truncate"
              data-testid="account-role-label"
            >
              {staffLabel(user?.role)}
            </div>
          )}
          <h1 className="font-serif text-xl sm:text-2xl text-espresso -mt-0.5 truncate">Мой кабинет</h1>
        </div>
        {(user?.role === "admin" || user?.role === "editor") && (
          <button
            onClick={() => nav("/admin")}
            data-testid={user?.role === "editor" ? "editor-workspace-entry" : "admin-workspace-entry"}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-espresso/40 bg-espresso/5 hover:bg-espresso/10 p-2 sm:px-3 sm:py-1.5 text-xs uppercase tracking-widest text-espresso whitespace-nowrap"
            title={user?.role === "editor" ? "Панель куратора" : "Панель управления"}
            aria-label={user?.role === "editor" ? "Панель куратора" : "Панель управления"}
          >
            <LayoutDashboard className="w-4 h-4 sm:w-3.5 sm:h-3.5 shrink-0" />
            <span className="hidden sm:inline">{user?.role === "editor" ? "Панель куратора" : "Панель управления"}</span>
          </button>
        )}
        <NotificationBell testid="account-notification-bell" />
        <button
          onClick={logout}
          data-testid="logout-button"
          className="shrink-0 text-xs uppercase tracking-widest text-muted-foreground hover:text-espresso whitespace-nowrap"
        >
          Выйти
        </button>
      </header>

      <div className="px-5 pb-8 flex-1 max-w-6xl mx-auto w-full">
        <Tabs value={tab} onValueChange={changeTab}>
          {/* Mobile: compact section selector */}
          <div className="sm:hidden mb-3">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Раздел</label>
            <Select value={tab} onValueChange={changeTab}>
              <SelectTrigger className="mt-1 h-11 rounded-2xl bg-white" data-testid="account-mobile-section-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SECTIONS.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`account-mobile-section-option-${s.id}`}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Desktop: 8-section tab bar */}
          <div className="hidden sm:block overflow-x-auto -mx-1 px-1">
            <TabsList className="rounded-full bg-sand/40 h-12 p-1 inline-flex w-max">
              {SECTIONS.map((s) => (
                <AccountTab key={s.id} v={s.id} icon={s.Icon} label={s.label} testid={`tab-${s.id}`} />
              ))}
            </TabsList>
          </div>

          <TabsContent value="overview"  className="mt-5"><OverviewSection onGoto={changeTab} /></TabsContent>
          <TabsContent value="products"  className="mt-5"><MyProductsSection /></TabsContent>
          <TabsContent value="saved"     className="mt-5"><SavedSection /></TabsContent>
          <TabsContent value="liked"     className="mt-5"><LikedSection /></TabsContent>
          <TabsContent value="workbook"  className="mt-5"><WorkbookSection /></TabsContent>
          <TabsContent value="requests"  className="mt-5"><RequestsSection /></TabsContent>
          <TabsContent value="purchases" className="mt-5"><PurchasesSection /></TabsContent>
          <TabsContent value="access"    className="mt-5"><AccessSection /></TabsContent>
          <TabsContent value="profile"   className="mt-5"><ProfileSection /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AccountTab({ v, icon: Icon, label, testid }) {
  return (
    <TabsTrigger
      value={v}
      data-testid={testid}
      className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-espresso px-4 gap-1.5 min-h-[44px] whitespace-nowrap"
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="text-sm">{label}</span>
    </TabsTrigger>
  );
}

function roleLabel(r) {
  return r === "admin" ? "Администратор" : r === "editor" ? "Куратор" : "Ученик";
}

// Header-only staff label. Student sees nothing (no "Ученик" leakage).
function staffLabel(r) {
  if (r === "admin") return "Администратор";
  if (r === "editor") return "Куратор";
  return null;
}

function EmptyPanel({ text, testid }) {
  return (
    <div className="rounded-3xl bg-white border hairline px-6 py-10 text-center" data-testid={testid}>
      <p className="font-serif italic text-espresso/70 text-lg">{text}</p>
    </div>
  );
}

function ProfileSection() {
  const { user, refreshUser } = useAuth();
  const [displayName, setDisplayName] = useState(user?.display_name || user?.name || "");
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    setAvatar(user?.avatar || "");
    setDisplayName(user?.display_name || user?.name || "");
  }, [user?.avatar, user?.display_name, user?.name]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", { display_name: displayName });
      await refreshUser?.();
      toast.success("Профиль обновлён");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const pickFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (!ALLOWED_IMAGE_MIMES.includes(file.type)) {
      toast.error("Поддерживаются форматы JPG, PNG, WEBP");
      return;
    }
    if (file.size > MAX_COVER_BYTES) {
      toast.error("Файл слишком большой (максимум 15 МБ)");
      return;
    }
    // Instant local preview while upload runs.
    const localUrl = URL.createObjectURL(file);
    setAvatar(localUrl);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/account/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAvatar(data.avatar || "");
      await refreshUser?.();
      toast.success("Аватар обновлён");
    } catch (err) {
      toast.error(formatApiError(err));
      setAvatar(user?.avatar || "");
    } finally {
      URL.revokeObjectURL(localUrl);
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    setRemoving(true);
    try {
      await api.delete("/account/avatar");
      setAvatar("");
      await refreshUser?.();
      toast.success("Аватар удалён");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRemoving(false);
    }
  };

  const initials = (displayName || user?.email || "?").trim().slice(0, 1).toUpperCase();

  return (
    <div className="rounded-3xl bg-white border hairline p-5 sm:p-6" data-testid="profile-section">
      <div className="flex flex-col sm:flex-row sm:items-start sm:gap-6 gap-5">
        {/* Avatar block */}
        <div className="flex flex-col items-center sm:items-start gap-3 shrink-0">
          <div
            className="relative w-[108px] h-[108px] rounded-full overflow-hidden border hairline bg-sand/40 shadow-sm"
            data-testid="profile-avatar-preview"
          >
            {avatar ? (
              <img
                src={avatar}
                alt="Аватар"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
                data-testid="profile-avatar-image"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center font-serif text-espresso/60 text-4xl select-none"
                data-testid="profile-avatar-placeholder"
                aria-label="Плейсхолдер аватара"
              >
                <User className="w-10 h-10" />
              </div>
            )}
            {uploading && (
              <div
                className="absolute inset-0 bg-white/70 flex items-center justify-center text-[11px] uppercase tracking-widest text-espresso"
                data-testid="profile-avatar-uploading"
              >
                Загрузка…
              </div>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            className="hidden"
            onChange={onFile}
            data-testid="profile-avatar-file"
          />

          <div className="flex flex-col items-center sm:items-start gap-2 w-full">
            <Button
              type="button"
              onClick={pickFile}
              disabled={uploading}
              className="btn-pill min-h-[40px] w-full sm:w-auto whitespace-nowrap bg-[#F2EADF] hover:bg-[#EADFCF] text-espresso border border-espresso/25 shadow-none font-medium"
              data-testid={avatar ? "profile-avatar-replace" : "profile-avatar-upload"}
            >
              {avatar ? "Заменить фото" : "Загрузить фото"}
            </Button>

            {avatar && (
              <button
                type="button"
                onClick={removeAvatar}
                disabled={removing || uploading}
                className="text-xs uppercase tracking-widest text-muted-foreground hover:text-red-500 disabled:opacity-50 min-h-[36px]"
                data-testid="profile-avatar-remove"
              >
                {removing ? "Удаление…" : "Удалить фото"}
              </button>
            )}

            <p className="text-[11px] text-muted-foreground text-center sm:text-left max-w-[220px] leading-snug">
              JPG, PNG или WEBP, до 15 МБ.
            </p>
          </div>
        </div>

        {/* Profile fields */}
        <div className="flex-1 min-w-0 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Email</label>
            <div className="text-espresso mt-1 break-all" data-testid="profile-email">{user?.email}</div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Имя</label>
            <Input
              data-testid="profile-name-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1 h-11 rounded-2xl"
              placeholder="Как вас называть"
            />
          </div>
          <Button
            onClick={save}
            disabled={saving}
            className="btn-pill min-h-[40px] bg-[#D9E4C8] hover:bg-[#CFDCBB] text-[#3B4A2A] border border-[#B8C7A0] shadow-none font-medium"
            data-testid="profile-save-button"
          >
            {saving ? "Сохраняем…" : "Сохранить"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ProjectsSection() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/projects");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/constructions/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Проект удалён");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">
        Загружаем архив…
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-3xl bg-white border hairline px-6 py-10 text-center">
        <p className="font-serif italic text-espresso/70 text-lg">
          Пока нет сохранённых проектов
        </p>
        <Button
          onClick={() => nav("/architect")}
          className="btn-pill bg-espresso hover:bg-espressoDark text-white mt-4"
          data-testid="account-start-architect-button"
        >
          Начать проект
        </Button>
      </div>
    );
  }

  return (
    <ul className="space-y-3" data-testid="account-projects-list">
      {items.map((c) => (
        <li
          key={c.id}
          data-testid={`account-project-item-${c.id}`}
          className="rounded-2xl bg-white border hairline p-4 flex items-center gap-3 hover:border-gold transition-colors"
        >
          <button
            onClick={() => nav(`/architect/${c.id}`)}
            className="flex-1 text-left"
            data-testid={`account-open-project-${c.id}`}
          >
            <div className="font-serif text-lg text-espresso">{c.name}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {c.layers.length} слоёв · {c.total_thickness_cm.toFixed(1)} см
            </div>
          </button>
          <div className="flex -space-x-1">
            {c.layers.slice(-4).map((l, i) => (
              <span
                key={l.id + i}
                className="w-6 h-6 rounded-full border-2 border-white"
                style={{ background: l.color }}
              />
            ))}
          </div>
          <button
            onClick={() => remove(c.id)}
            data-testid={`account-delete-project-${c.id}`}
            className="p-2 rounded-full hover:bg-red-50"
            aria-label="Удалить"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}
function accessDurationLabel(g) {
  if (g?.access_type === "lifetime") return "Бессрочный доступ";
  if (g?.end_at) return `Доступ до ${fmtDate(g.end_at)}`;
  return "Активный доступ";
}
function paymentStatusLabel(s) {
  return { paid: "Оплачено", succeeded: "Оплачено", pending: "В обработке", failed: "Ошибка", refunded: "Возвращено" }[s] || s || "—";
}
function fmtAmount(minor, currency) {
  if (typeof minor !== "number") return "—";
  const val = (minor / 100).toFixed(2);
  const cur = (currency || "").toUpperCase();
  return cur ? `${val} ${cur}` : val;
}

function ProductCard({ item, onOpen, testid }) {
  const durationLabel = accessDurationLabel(item);
  return (
    <li data-testid={testid}>
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left rounded-2xl bg-white border hairline p-4 hover:bg-sand/20 hover:shadow-sm transition min-h-[80px] flex items-center gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="font-serif text-lg text-espresso truncate">{item.product_title || "Продукт"}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{durationLabel}</div>
        </div>
        <span className="chip bg-espresso/10 text-espresso border border-espresso/20 text-[11px] uppercase tracking-widest whitespace-nowrap">Открыть</span>
      </button>
    </li>
  );
}

function volumeTargetFor(g) {
  const type = (g.product_type || "").toUpperCase();
  if (type === "VOLUME") {
    return `/book/${encodeURIComponent(g.product_slug || g.product_id)}/entry`;
  }
  return `/products/${encodeURIComponent(g.product_slug || g.product_id)}`;
}

function OverviewSection({ onGoto }) {
  const nav = useNavigate();
  const [access, setAccess] = useState([]);
  const [saved, setSaved] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [a, s, p] = await Promise.all([
          api.get("/account/access"),
          api.get("/account/saved"),
          api.get("/account/projects"),
        ]);
        setAccess(a.data || []);
        setSaved(s.data || []);
        setProjects(p.data || []);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  const activeAccess = access.filter((g) => g.status === "active" || !g.status).slice(0, 3);
  const recentSaved = (saved || []).slice(0, 3);
  const recentProjects = (projects || []).slice(0, 3);
  return (
    <div className="space-y-6" data-testid="overview-section">
      <section data-testid="overview-products">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="font-serif text-xl text-espresso">Мои активные продукты</h2>
          {activeAccess.length > 0 && (
            <button onClick={() => onGoto("products")} className="text-xs uppercase tracking-widest text-espresso/70 hover:text-espresso">Все</button>
          )}
        </div>
        {activeAccess.length === 0 ? (
          <EmptyPanel text="Пока нет активных продуктов. Они появятся после первой покупки." testid="overview-products-empty" />
        ) : (
          <ul className="space-y-2">
            {activeAccess.map((g) => (
              <ProductCard key={g.id} item={g} testid={`overview-product-${g.id}`}
                onOpen={() => nav(volumeTargetFor(g))} />
            ))}
          </ul>
        )}
      </section>

      <section data-testid="overview-saved">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="font-serif text-xl text-espresso">Последнее сохранённое</h2>
          {recentSaved.length > 0 && (
            <button onClick={() => onGoto("saved")} className="text-xs uppercase tracking-widest text-espresso/70 hover:text-espresso">Все</button>
          )}
        </div>
        {recentSaved.length === 0 ? (
          <EmptyPanel text="Сохранённых материалов пока нет." testid="overview-saved-empty" />
        ) : (
          <ul className="space-y-2">
            {recentSaved.map((m) => (
              <li key={m.id || m.material_id}>
                <button
                  onClick={() => nav(`/library/${encodeURIComponent(m.material_slug || m.material_id)}`)}
                  className="w-full text-left rounded-2xl bg-white border hairline p-3 hover:bg-sand/20 transition"
                  data-testid={`overview-saved-${m.id || m.material_id}`}
                >
                  <div className="font-serif text-espresso truncate">{m.material_title || m.title || "Материал"}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section data-testid="overview-projects">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="font-serif text-xl text-espresso">Последние проекты</h2>
          {recentProjects.length > 0 && (
            <button onClick={() => onGoto("workbook")} className="text-xs uppercase tracking-widest text-espresso/70 hover:text-espresso">Все</button>
          )}
        </div>
        {recentProjects.length === 0 ? (
          <EmptyPanel text="Проектов Архитектора пока нет." testid="overview-projects-empty" />
        ) : (
          <ul className="space-y-2">
            {recentProjects.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => nav(`/architect?project=${p.id}`)}
                  className="w-full text-left rounded-2xl bg-white border hairline p-3 hover:bg-sand/20 transition"
                  data-testid={`overview-project-${p.id}`}
                >
                  <div className="font-serif text-espresso truncate">{p.name || "Проект"}</div>
                  {p.updated_at && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">Обновлён {fmtDate(p.updated_at)}</div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function MyProductsSection() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/account/access"); setItems(data || []); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  const active = items.filter((g) => g.status === "active" || !g.status);
  if (active.length === 0) {
    return <EmptyPanel text="Пока нет доступных продуктов. Они появятся после первой покупки." testid="products-empty" />;
  }
  return (
    <ul className="space-y-3" data-testid="products-list">
      {active.map((g) => (
        <ProductCard key={g.id} item={g} testid={`product-item-${g.id}`}
          onOpen={() => nav(volumeTargetFor(g))} />
      ))}
    </ul>
  );
}

function PurchasesSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/account/payments"); setItems(data || []); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  if (items.length === 0) {
    return <EmptyPanel text="Покупок пока нет. Ваши платежи появятся здесь после первой оплаты." testid="purchases-empty" />;
  }
  return (
    <ul className="space-y-2" data-testid="purchases-list">
      {items.map((p) => (
        <li key={p.id} data-testid={`purchase-item-${p.id}`}
            className="rounded-2xl bg-white border hairline p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="min-w-0">
            <div className="font-serif text-espresso truncate">{p.product_title || "Продукт"}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(p.created_at)}</div>
          </div>
          <div className="flex items-center gap-3 whitespace-nowrap">
            <span className="font-serif text-espresso">{fmtAmount(p.amount_minor, p.currency)}</span>
            <span className="chip bg-sand/60 text-espresso text-[10px] uppercase tracking-widest">{paymentStatusLabel(p.status)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function AccessSection() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/account/access");
        setItems(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  if (items.length === 0) {
    return <EmptyPanel text="У вас пока нет активных доступов. Они появятся, когда откроются продукты студии." testid="access-empty" />;
  }
  return (
    <ul className="space-y-3" data-testid="access-list">
      {items.map((g) => {
        const durationLabel = g.access_type === "lifetime"
          ? "Бессрочный доступ"
          : (g.end_at ? `Доступ до ${new Date(g.end_at).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" })}` : "Ограниченный доступ");
        const target = volumeTargetFor(g);
        return (
          <li key={g.id} data-testid={`access-item-${g.id}`}>
            <button
              type="button"
              onClick={() => nav(target)}
              data-testid={`access-open-${g.id}`}
              className="w-full text-left rounded-2xl bg-white border hairline p-4 hover:bg-sand/20 hover:shadow-sm active:scale-[0.995] transition cursor-pointer min-h-[76px]"
            >
              <div className="font-serif text-lg text-espresso">{g.product_title || "Продукт"}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{durationLabel}</div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}


function SavedSection() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/account/saved");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (mid) => {
    try {
      await api.delete(`/account/saved/${mid}`);
      setItems((xs) => xs.filter((x) => x.id !== mid));
      toast.success("Удалено из «Сохранённое»");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const TYPE_LABEL = {
    ARTICLE: "Статья",
    RECIPE: "Рецепт",
    FORMULATION: "Рецептура",
    GUIDE: "Гайд",
    REFERENCE: "Справочник",
  };

  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  if (items.length === 0) {
    return <EmptyPanel text="Здесь будут статьи, рецепты и материалы, которые вы сохранили." testid="saved-empty" />;
  }
  return (
    <ul className="space-y-3" data-testid="saved-list">
      {items.map((m) => (
        <li
          key={m.id}
          className="rounded-2xl bg-white border hairline p-3 flex items-center gap-3"
          data-testid={`saved-item-${m.id}`}
        >
          <button
            onClick={() => nav(`/library/${m.slug || m.id}`)}
            className="w-14 h-14 rounded-xl bg-sand/40 overflow-hidden shrink-0"
            aria-label={`Открыть ${m.title}`}
          >
            {m.cover_image ? (
              <img src={m.cover_image} alt="" className="w-full h-full object-cover"
                   onError={(e) => { e.target.style.display = "none"; }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-serif text-espresso/40">A</div>
            )}
          </button>
          <button onClick={() => nav(`/library/${m.slug || m.id}`)} className="flex-1 min-w-0 text-left">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {TYPE_LABEL[m.material_type] || m.material_type}
            </div>
            <div className="font-serif text-espresso text-lg truncate leading-snug">{m.title}</div>
            {m.short_description && (
              <div className="text-xs text-muted-foreground truncate">{m.short_description}</div>
            )}
          </button>
          <button
            onClick={() => remove(m.id)}
            data-testid={`saved-remove-${m.id}`}
            className="p-2 rounded-full hover:bg-red-50"
            aria-label="Убрать из сохранённого"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </li>
      ))}
    </ul>
  );
}

// ---------- Понравившиеся ----------
const MATERIAL_TYPE_LABEL = {
  ARTICLE: "Статья", RECIPE: "Рецепт", FORMULATION: "Рецептура",
  GUIDE: "Гайд", REFERENCE: "Справочник",
};

function LikedSection() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    try { const { data } = await api.get("/account/likes"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const unlike = async (mid) => {
    try { await api.delete(`/account/likes/${mid}`); setItems((xs) => xs.filter((x) => x.id !== mid)); toast.success("Убрано"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  if (items.length === 0)
    return <EmptyPanel text="Здесь появятся материалы, которые вам понравились." testid="liked-empty" />;
  return (
    <ul className="space-y-3" data-testid="liked-list">
      {items.map((m) => (
        <li key={m.id} className="rounded-2xl bg-white border hairline p-3 flex items-center gap-3" data-testid={`liked-item-${m.id}`}>
          <button onClick={() => nav(`/library/${m.slug || m.id}`)} className="w-14 h-14 rounded-xl bg-sand/40 overflow-hidden shrink-0" aria-label={`Открыть ${m.title}`}>
            {m.cover_image ? <img src={m.cover_image} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }}/> :
              <div className="w-full h-full flex items-center justify-center font-serif text-espresso/40">A</div>}
          </button>
          <button onClick={() => nav(`/library/${m.slug || m.id}`)} className="flex-1 min-w-0 text-left">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{MATERIAL_TYPE_LABEL[m.material_type] || m.material_type}</div>
            <div className="font-serif text-espresso text-lg truncate leading-snug">{m.title}</div>
            {m.short_description && <div className="text-xs text-muted-foreground truncate">{m.short_description}</div>}
          </button>
          <button onClick={() => unlike(m.id)} data-testid={`liked-unlike-${m.id}`} className="p-2 rounded-full hover:bg-red-50 min-w-[44px] min-h-[44px] flex items-center justify-center" aria-label="Убрать">
            <Heart className="w-4 h-4 text-red-500 fill-red-500" />
          </button>
        </li>
      ))}
    </ul>
  );
}

// ---------- Мои обращения (Sprint 39) ----------
const REQUEST_STATUS_USER_LABEL = {
  new: "Новое",
  in_progress: "В работе",
  waiting_for_user: "Ждём Вашего ответа",
  closed: "Закрыто",
};

const REQUEST_STATUS_TONE = {
  new: "bg-[#F7EDDA] text-[#7A4A1F] border-[#E1CFA8]",
  in_progress: "bg-[#E7DEF7] text-[#4A3374] border-[#CBBCE7]",
  waiting_for_user: "bg-[#FCE7C6] text-[#8A4D14] border-[#F0CE8B]",
  closed: "bg-[#E6E6E6] text-[#565656] border-[#CFCFCF]",
};

function StatusChip({ status, staff = false, testid }) {
  const label = staff && status === "waiting_for_user" ? "Ждём пользователя" : (REQUEST_STATUS_USER_LABEL[status] || status);
  const cls = REQUEST_STATUS_TONE[status] || "bg-sand/60 text-espresso border-sand";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${cls}`} data-testid={testid}>
      {label}
    </span>
  );
}

function AvatarBubble({ src, name, size = 32 }) {
  const dim = { width: size, height: size };
  const initial = (name || "?").trim().slice(0, 1).toUpperCase();
  return (
    <div
      className="rounded-full bg-sand/50 overflow-hidden border hairline flex items-center justify-center text-espresso/60 font-serif shrink-0"
      style={dim}
      aria-hidden="true"
    >
      {src ? <img src={src} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : initial}
    </div>
  );
}

function RequestsSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");

  if (openId) {
    return <RequestDetailView rid={openId} onBack={() => {
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }} />;
  }
  return <RequestsListView onOpen={(id) => {
    const next = new URLSearchParams(searchParams);
    next.set("open", id);
    setSearchParams(next, { replace: true });
  }} />;
}

function RequestsListView({ onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/account/requests"); setItems(data || []); }
      catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  if (items.length === 0) return <EmptyPanel text="У Вас пока нет обращений." testid="requests-empty" />;
  return (
    <ul className="space-y-2" data-testid="requests-list">
      {items.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            onClick={() => onOpen(r.id)}
            className="w-full text-left rounded-2xl bg-white border hairline p-4 hover:bg-sand/20 hover:shadow-sm transition min-h-[80px]"
            data-testid={`request-item-${r.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-serif text-espresso text-lg truncate">{r.material_title || "Материал"}</div>
                {r.last_message_preview && (
                  <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{r.last_message_preview}</div>
                )}
                <div className="text-[11px] text-muted-foreground mt-1">{fmtDate(r.last_message_at || r.updated_at)}</div>
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <StatusChip status={r.status} testid={`request-item-status-${r.id}`} />
                {(r.user_unread_count || 0) > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#B84A2A] text-white text-[10px]" data-testid={`request-item-unread-${r.id}`}>
                    {r.user_unread_count}
                  </span>
                )}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

function RequestDetailView({ rid, onBack }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/account/requests/${rid}`); setReq(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [rid]); // eslint-disable-line

  const send = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const { data } = await api.post(`/account/requests/${rid}/reply`, { body: trimmed });
      setReq(data); setBody("");
      toast.success("Сообщение отправлено");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  if (loading) return <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p>;
  if (!req) return <EmptyPanel text="Обращение не найдено." testid="request-detail-not-found" />;

  const openMaterial = () => {
    if (req.material_slug) nav(`/library/${encodeURIComponent(req.material_slug)}`);
    else toast.error("Ссылка на материал недоступна");
  };
  const isClosed = req.status === "closed";

  return (
    <div className="space-y-4" data-testid="request-detail">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onBack} data-testid="request-detail-back" className="chip bg-white border border-espresso/25 text-espresso px-3 py-1.5 hover:bg-sand/40 inline-flex items-center gap-1.5">
          <ChevronLeft className="w-3.5 h-3.5" /> К списку
        </button>
        <StatusChip status={req.status} testid="request-detail-status" />
      </div>

      <div className="rounded-3xl bg-white border hairline p-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Материал</div>
        <div className="font-serif text-xl text-espresso truncate mt-0.5" data-testid="request-detail-material-title">{req.material_title || "—"}</div>
        {(req.product_title || req.section_title) && (
          <div className="text-xs text-muted-foreground mt-0.5">
            {req.product_title}{req.section_title ? ` · ${req.section_title}` : ""}
          </div>
        )}
        <button onClick={openMaterial} className="mt-3 text-xs uppercase tracking-widest text-espresso/80 hover:text-espresso" data-testid="request-open-material">
          Открыть материал →
        </button>
      </div>

      <ul className="space-y-3" data-testid="request-detail-messages">
        {req.messages.map((m) => (
          <li key={m.id} className={`flex gap-3 ${m.sender_type === "user" ? "justify-end" : ""}`} data-testid={`request-message-${m.id}`}>
            {m.sender_type === "staff" && <AvatarBubble src={m.sender_avatar} name={m.sender_name} />}
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${m.sender_type === "user" ? "bg-[#F2EADF] border border-[#E1D2B8] text-espresso" : "bg-white border hairline text-espresso"}`}>
              <div className="text-[11px] text-muted-foreground mb-0.5">
                <span className="font-medium text-espresso">{m.sender_name || (m.sender_type === "staff" ? "Команда" : "Вы")}</span>
                {m.sender_type === "staff" && <span> · Команда Anokina Studio</span>}
                <span> · {fmtDate(m.created_at)}</span>
              </div>
              <div className="font-serif whitespace-pre-wrap leading-snug">{m.body}</div>
            </div>
            {m.sender_type === "user" && <AvatarBubble src={user?.avatar} name={user?.display_name || user?.name} />}
          </li>
        ))}
      </ul>

      {isClosed ? (
        <div className="rounded-2xl bg-sand/40 border hairline p-3 text-sm text-muted-foreground text-center" data-testid="request-detail-closed-notice">
          Обращение закрыто. История доступна для чтения.
        </div>
      ) : (
        <div className="rounded-2xl bg-white border hairline p-3">
          <label className="text-xs uppercase tracking-widest text-muted-foreground">Написать сообщение</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2 text-sm font-serif text-espresso focus:outline-none focus:ring-2 focus:ring-espresso/20"
            placeholder="Ваше сообщение…"
            data-testid="request-reply-input"
            disabled={sending}
          />
          <div className="mt-2 flex justify-end">
            <Button
              onClick={send}
              disabled={sending || !body.trim()}
              className="btn-pill min-h-[40px] bg-[#D9E4C8] hover:bg-[#CFDCBB] text-[#3B4A2A] border border-[#B8C7A0] shadow-none font-medium"
              data-testid="request-reply-send"
            >
              {sending ? "Отправляем…" : "Отправить"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Мои материалы (private) ----------
const USER_MATERIAL_TYPES = [
  ["RECIPE", "Рецепт"], ["NOTE", "Заметка"], ["ADAPTATION", "Адаптация"],
  ["VEGAN", "Веганская версия"], ["PREP", "Заготовка"], ["OTHER", "Другое"],
];
const UMT_LABEL = Object.fromEntries(USER_MATERIAL_TYPES);

function MyMaterialsSection() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("search", q.trim());
      if (type) params.set("material_type", type);
      const { data } = await api.get(`/account/materials${params.toString() ? `?${params}` : ""}`);
      setItems(data);
      return data;
    } catch (e) { toast.error(formatApiError(e)); return []; }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, type]); // eslint-disable-line

  // Support deep-link ?edit=<id> to open the editor dialog on mount
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/account/materials/${editId}`);
        setEditing(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete("edit");
        setSearchParams(next, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (data) => {
    try {
      if (data.id) {
        const { id, created_at, updated_at, owner_user_id, ...body } = data;
        await api.patch(`/account/materials/${id}`, body);
        toast.success("Сохранено");
      } else {
        await api.post("/account/materials", data);
        toast.success("Создано");
      }
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/account/materials/${id}`); toast.success("Удалено"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const duplicate = async (id) => {
    try { await api.post(`/account/materials/${id}/duplicate`); toast.success("Дублировано"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="my-materials">
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input placeholder="Поиск" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-2xl h-10 bg-white" data-testid="my-materials-search" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="my-materials-type-filter"
                className="h-10 rounded-2xl border border-input bg-white px-3 text-sm">
          <option value="">Все типы</option>
          {USER_MATERIAL_TYPES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Button onClick={() => setEditing({ material_type: "NOTE", tags: [] })} data-testid="my-materials-new"
          className="btn-pill bg-espresso hover:bg-espressoDark text-white h-auto py-2 px-4 text-sm">
          <Plus className="w-4 h-4 mr-1" /> Создать
        </Button>
      </div>

      {loading ? <p className="text-center text-sm text-muted-foreground py-10 font-serif italic">Загрузка…</p> :
       items.length === 0 ? <EmptyPanel text="Ваши личные рецепты, адаптации и заметки появятся здесь." testid="my-materials-empty" /> :
        <ul className="space-y-2" data-testid="my-materials-list">
          {items.map((m) => (
            <li key={m.id} className="rounded-2xl bg-white border hairline p-3 flex items-center gap-2" data-testid={`my-material-${m.id}`}>
              <button onClick={() => nav(`/account/materials/${m.id}`)} className="w-14 h-14 rounded-xl bg-sand/40 overflow-hidden shrink-0" aria-label={`Открыть ${m.title || "материал"}`} data-testid={`my-material-cover-${m.id}`}>
                {m.cover_image ? (
                  <img src={m.cover_image} alt="" className="w-full h-full object-cover"
                       onError={(e) => { e.target.style.display = "none"; }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-serif text-espresso/40">A</div>
                )}
              </button>
              <button onClick={() => nav(`/account/materials/${m.id}`)} className="flex-1 min-w-0 text-left hover:bg-sand/20 rounded-xl px-2 py-1" data-testid={`my-material-open-${m.id}`}>
                <div className="font-medium text-espresso truncate">{m.title}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {UMT_LABEL[m.material_type] || m.material_type}
                  {m.tags?.length ? ` · ${m.tags.slice(0,3).join(", ")}` : ""}
                </div>
              </button>
              <button onClick={() => duplicate(m.id)} className="p-2 rounded-full hover:bg-sand/50 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid={`my-material-duplicate-${m.id}`} title="Дублировать"><Copy className="w-4 h-4 text-espresso" /></button>
              <button onClick={() => setEditing(m)} className="p-2 rounded-full hover:bg-sand/50 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid={`my-material-edit-${m.id}`} title="Редактировать"><Pencil className="w-4 h-4 text-espresso" /></button>
              <button onClick={() => remove(m.id)} className="p-2 rounded-full hover:bg-red-50 min-w-[44px] min-h-[44px] flex items-center justify-center" data-testid={`my-material-delete-${m.id}`} title="Удалить"><Trash2 className="w-4 h-4 text-red-500" /></button>
            </li>
          ))}
        </ul>}

      <MyMaterialEditor open={!!editing} data={editing} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

const ALLOWED_IMAGE_MIMES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_COVER_BYTES = 15 * 1024 * 1024;

function MyMaterialEditor({ open, data, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const fileRef = React.useRef(null);
  useEffect(() => { setForm(data || {}); setErrors({}); }, [data]);
  if (!open) return null;
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((prev) => { const n = { ...prev }; delete n[k]; return n; });
  };

  const uploadCover = async (file) => {
    if (!ALLOWED_IMAGE_MIMES.includes(file.type)) {
      toast.error("Поддерживаются форматы JPG, PNG, WEBP");
      return;
    }
    if (file.size > MAX_COVER_BYTES) {
      toast.error("Файл слишком большой (максимум 15 МБ)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      const { data } = await api.post("/account/media/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("cover_image", data.file_url);
      toast.success("Обложка загружена");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const removeCover = () => set("cover_image", "");

  const validate = () => {
    const errs = {};
    if (!form.title || !form.title.trim()) errs.title = "Введите название материала";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = () => {
    if (!validate()) {
      toast.error("Проверьте обязательные поля");
      return;
    }
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="my-material-editor">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-espresso">{form.id ? "Мой материал" : "Новый материал"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Название</label>
            <Input
              value={form.title || ""}
              onChange={(e) => set("title", e.target.value)}
              className={`mt-1 rounded-2xl ${errors.title ? "border-red-400 focus-visible:ring-red-400" : ""}`}
              data-testid="my-material-title"
              aria-invalid={!!errors.title}
              placeholder="Например, Кокосовый бисквит"
            />
            {errors.title && (
              <p className="text-xs text-red-500 mt-1" data-testid="my-material-title-error">{errors.title}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Тип</label>
              <select value={form.material_type || "NOTE"} onChange={(e) => set("material_type", e.target.value)} data-testid="my-material-type"
                      className="mt-1 h-10 w-full rounded-2xl border border-input bg-white px-3 text-sm">
                {USER_MATERIAL_TYPES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Теги (через запятую)</label>
              <Input value={(form.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="mt-1 rounded-2xl" data-testid="my-material-tags" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Краткое описание</label>
            <Input value={form.short_description || ""} onChange={(e) => set("short_description", e.target.value)} className="mt-1 rounded-2xl" data-testid="my-material-short" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Обложка</label>
            <div className="mt-1 flex items-center gap-3">
              {form.cover_image ? (
                <img src={form.cover_image} alt="" className="w-20 h-20 rounded-2xl object-cover border hairline" data-testid="my-material-cover-preview" />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-sand/40 border hairline" />
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadCover(e.target.files[0])} data-testid="my-material-cover-file" />
              <Button type="button" variant="outline" className="btn-pill" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="my-material-cover-upload">
                {uploading ? "Загрузка…" : (form.cover_image ? "Заменить" : "Загрузить")}
              </Button>
              {form.cover_image && (
                <Button type="button" variant="ghost" className="btn-pill text-red-500" onClick={removeCover} data-testid="my-material-cover-remove">
                  Убрать
                </Button>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">JPG, PNG или WEBP, до 15 МБ.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground">Содержание</label>
            <textarea value={form.body || ""} onChange={(e) => set("body", e.target.value)} rows={10}
              className="mt-1 w-full rounded-2xl border border-input bg-white px-3 py-2 text-sm font-serif" data-testid="my-material-body" placeholder="Добавьте рецепт, заметки или технологию" />
            <p className="text-[11px] text-muted-foreground mt-1">Добавьте рецепт, заметки или технологию. Ваши материалы приватны.</p>
          </div>
        </div>
        <DialogFooter className="mt-3 gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={onClose} className="btn-pill">Закрыть</Button>
          <Button onClick={submit} className="btn-pill bg-espresso text-white" data-testid="my-material-save">Сохранить</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

